import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WelcomeScreen } from './WelcomeScreen';
import { ModeSelection } from './ModeSelection';
import { ValueStorySlides } from './ValueStorySlides';
import { OnboardingCarousel } from './OnboardingCarousel';
import { ProfileSetup } from './ProfileSetup';
import { PermissionsRequest } from './PermissionsRequest';
import { InteractiveTutorial } from './InteractiveTutorial';
import { AAKConsentJourney } from '../consent/AAKConsentJourney';

type OnboardingStep = 'welcome' | 'modeSelection' | 'valueStory' | 'carousel' | 'profile' | 'consent' | 'permissions' | 'tutorial' | 'complete';

export const OnboardingFlow = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  // Check if user has completed onboarding
  useEffect(() => {
    const completed = localStorage.getItem('onboarding_completed');
    if (completed === 'true') {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleWelcomeContinue = () => {
    setCurrentStep('modeSelection');
  };

  const handleModeSelectionComplete = () => {
    setCurrentStep('valueStory');
  };

  const handleValueStoryComplete = () => {
    setCurrentStep('carousel');
  };

  const handleValueStorySkip = () => {
    // Mark onboarding as complete and navigate to home
    localStorage.setItem('onboarding_completed', 'true');
    navigate('/', { replace: true });
  };

  const handleCarouselComplete = () => {
    setCurrentStep('profile');
  };

  const handleProfileComplete = () => {
    setCurrentStep('consent');
  };

  const handleConsentComplete = () => {
    setCurrentStep('permissions');
  };

  const handlePermissionsComplete = () => {
    setCurrentStep('tutorial');
  };

  const handleTutorialComplete = () => {
    // Mark onboarding as complete
    localStorage.setItem('onboarding_completed', 'true');
    setHasCompletedOnboarding(true);
    navigate('/', { replace: true });
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true');
    navigate('/', { replace: true });
  };

  // Tutorial steps
  const tutorialSteps = [
    {
      id: 'camera',
      target: '[data-tour="camera-button"]',
      title: 'Capture Activities',
      description: 'Tap here to take photos of your farm activities',
      position: 'bottom' as const,
    },
    {
      id: 'score',
      target: '[data-tour="score-badge"]',
      title: 'Track Your Score',
      description: 'Earn points and badges for your activities',
      position: 'bottom' as const,
    },
    {
      id: 'activities',
      target: '[data-tour="activity-log"]',
      title: 'View Your Timeline',
      description: 'See all your recorded activities here',
      position: 'top' as const,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {currentStep === 'welcome' && (
        <WelcomeScreen
          onGetStarted={handleWelcomeContinue}
          onSignIn={() => navigate('/auth')}
        />
      )}

      {currentStep === 'modeSelection' && (
        <ModeSelection
          onComplete={handleModeSelectionComplete}
        />
      )}

      {currentStep === 'valueStory' && (
        <ValueStorySlides
          onComplete={handleValueStoryComplete}
          onSkip={handleValueStorySkip}
        />
      )}

      {currentStep === 'carousel' && (
        <OnboardingCarousel
          onComplete={handleCarouselComplete}
          onSkip={handleSkip}
        />
      )}

      {currentStep === 'profile' && (
        <ProfileSetup
          onComplete={handleProfileComplete}
          onSkip={handleSkip}
        />
      )}

      {currentStep === 'consent' && (
        <AAKConsentJourney
          mode="onboarding"
          startScreen={1}
          onComplete={handleConsentComplete}
          onSkip={handleConsentComplete}
        />
      )}

      {currentStep === 'permissions' && (
        <PermissionsRequest
          onComplete={handlePermissionsComplete}
          onSkip={handleSkip}
        />
      )}

      {currentStep === 'tutorial' && (
        <InteractiveTutorial
          steps={tutorialSteps}
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialComplete}
        />
      )}
    </div>
  );
};
